import React from 'react'

import { Box, HStack, Text } from '@chakra-ui/react'
import {
  AgoraRTCReactError,
  ICameraVideoTrack,
  IMicrophoneAudioTrack,
} from 'agora-rtc-react'
import { CheckDone, CheckUndone, Error } from 'src/components/icon'

import {
  NetworkTestResult,
  NetworkTestState,
} from '../../hooks/useTwoClientNetworkTest'

type CheckStatus = 'pending' | 'ok' | 'error'

/** 品質スコア 1〜3 を正常、0（不明）および 4 以上を異常とする */
const isQualityAcceptable = (score: 0 | 1 | 2 | 3 | 4 | 5 | 6): boolean => {
  return score >= 1 && score <= 3
}

const getNetworkStatus = (
  state: NetworkTestState,
  result: NetworkTestResult | null,
): CheckStatus => {
  if (state === 'idle' || state === 'testing') return 'pending'
  if (state === 'error') return 'error'
  if (
    result !== null &&
    isQualityAcceptable(result.uplinkQuality) &&
    isQualityAcceptable(result.downlinkQuality) &&
    result.packetLossRate < 0.05
  ) {
    return 'ok'
  }
  return 'error'
}

type StatusIconProps = { status: CheckStatus }

const StatusIcon: React.FC<StatusIconProps> = ({ status }) => {
  if (status === 'pending')
    return (
      <Box color="gray.400">
        <CheckUndone />
      </Box>
    )
  if (status === 'ok')
    return (
      <Box color="green.500">
        <CheckDone />
      </Box>
    )
  return (
    <Box color="red.500">
      <Error />
    </Box>
  )
}

type Props = {
  localCameraTrack: ICameraVideoTrack | null
  localCameraError: AgoraRTCReactError | null
  localMicrophoneTrack: IMicrophoneAudioTrack | null
  localMicrophoneError: AgoraRTCReactError | null
  networkTestState: NetworkTestState
  networkResult: NetworkTestResult | null
}

export const ConnectionTestResultSummary: React.FC<Props> = ({
  localCameraTrack,
  localCameraError,
  localMicrophoneTrack,
  localMicrophoneError,
  networkTestState,
  networkResult,
}) => {
  const cameraStatus: CheckStatus = localCameraError
    ? 'error'
    : localCameraTrack
      ? 'ok'
      : 'pending'

  const micStatus: CheckStatus = localMicrophoneError
    ? 'error'
    : localMicrophoneTrack
      ? 'ok'
      : 'pending'

  const networkStatus = getNetworkStatus(networkTestState, networkResult)

  const allOk =
    cameraStatus === 'ok' && micStatus === 'ok' && networkStatus === 'ok'

  return (
    <Box borderTop="1px solid" borderColor="gray.200" pt="4" mt="2">
      <HStack spacing="6" flexWrap="wrap">
        <HStack spacing="1">
          <StatusIcon status={cameraStatus} />
          <Text fontSize="sm">カメラ</Text>
        </HStack>
        <HStack spacing="1">
          <StatusIcon status={micStatus} />
          <Text fontSize="sm">マイク</Text>
        </HStack>
        <HStack spacing="1">
          <StatusIcon status={networkStatus} />
          <Text fontSize="sm">ネットワーク</Text>
        </HStack>
        {allOk && (
          <Text fontSize="sm" color="green.600" fontWeight="bold">
            すべて正常です
          </Text>
        )}
        {networkStatus === 'error' && (
          <Text fontSize="sm" color="red.500">
            接続に問題があります。何度かテストを行ってみてください。
          </Text>
        )}
      </HStack>
    </Box>
  )
}
